import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAdminAuth } from "../../lib/auth";
import { LOGO_URL } from "../../data/products";
import { Lock, ArrowRight } from "lucide-react";
import { toast } from "sonner";

export default function AdminLogin() {
  const { admin, login, loading } = useAdminAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  if (!loading && admin) return <Navigate to="/admin" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    const res = await login(email.trim(), password);
    setSubmitting(false);
    if (res.ok) {
      toast.success("Welcome back ♡");
      navigate("/admin", { replace: true });
    } else {
      setError(res.error);
    }
  };

  return (
    <div className="min-h-screen bg-cream-100 grid place-items-center px-6 relative overflow-hidden">
      <div className="blob bg-blush-200 w-[460px] h-[460px] -top-40 -left-20" />
      <div className="blob bg-blush-100 w-[400px] h-[400px] bottom-0 -right-20" />
      <div className="relative w-full max-w-md bg-white rounded-[2rem] border border-blush-100 shadow-blush p-10">
        <div className="flex items-center gap-3">
          <img src={LOGO_URL} alt="Flownté" className="h-12 w-12 rounded-full ring-1 ring-blush-200" />
          <div>
            <div className="font-script text-3xl text-blush-600 leading-none">Flownté</div>
            <div className="text-[10px] uppercase tracking-[0.28em] text-ink-500">Atelier Console</div>
          </div>
        </div>
        <h1 className="mt-8 font-heading text-3xl text-ink-900">Sign in</h1>
        <p className="mt-2 font-body text-ink-500 text-sm">
          Manage products, orders, and integrations.
        </p>

        <form onSubmit={onSubmit} className="mt-8 space-y-4" data-testid="admin-login-form">
          <div>
            <label className="text-xs font-body uppercase tracking-[0.2em] text-ink-500">Email</label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="field mt-2"
              placeholder="admin@flownte.in"
              data-testid="admin-login-email"
            />
          </div>
          <div>
            <label className="text-xs font-body uppercase tracking-[0.2em] text-ink-500">Password</label>
            <input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="field mt-2"
              placeholder="••••••••"
              data-testid="admin-login-password"
            />
          </div>
          {error && (
            <div className="rounded-2xl bg-blush-50 border border-blush-200 px-4 py-3 text-sm text-blush-700 font-body" data-testid="admin-login-error">
              {error}
            </div>
          )}
          <button
            type="submit"
            disabled={submitting}
            data-testid="admin-login-submit"
            className="btn-maroon w-full inline-flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {submitting ? "Signing in…" : (<>Sign in <ArrowRight size={14} strokeWidth={1.6} /></>)}
          </button>
        </form>
        <p className="mt-6 text-[11px] font-body text-ink-500 inline-flex items-center gap-2">
          <Lock size={11} strokeWidth={1.6} /> Encrypted session · Bearer token
        </p>
      </div>
    </div>
  );
}
